/**
 * @design-contract CUR-113 + CUR-206
 *
 * Renders a single chat message. Carries CUR-206 TTS placement
 * invariants (TTS button only on last text-of-turn, concatenates all
 * text segments for playback) and the cluster-end inset rule. The
 * actual rules live in `~/components/chat/design/`; this file
 * consumes them via named imports.
 */
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Code2,
  GitPullRequestCreateArrow,
  Loader2,
  MessageSquare,
  Pause,
  Play,
  Square,
  Volume2
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  AssistantResponseFeedback,
  responseActionButtonClassName
} from '~/components/chat/AssistantResponseFeedback'
import { CanvasCardsContainer } from '~/components/chat/CanvasCardsContainer'
import { CanvasFileViewer } from '~/components/chat/CanvasFileViewer'
import { ChatMarkdown } from '~/components/chat/ChatMarkdown'
import { ChatProgressMessageView } from '~/components/chat/ChatProgressMessageView'
import type { ChatMessage } from '~/components/chat/chatTypes'
import {
  assistantTextClusterClass,
  getTurnTextSegments,
  shouldApplyClusterEndInset,
  ASSISTANT_CLUSTER_END_INSET_CLASS
} from '~/components/chat/design'
import type { CanvasItem } from '~/components/chat/message'
import type { ChatSelectionWidget } from '~/components/chat/message'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import { stripMarkdown } from '~/utils/stripMarkdown'
import {
  getOddDoubleAsteriskTableRows,
  getRawMarkdownMarkerLocations,
  getTextDeltaBoundary,
  isStreamingMarkdownDebugEnabled,
  useStreamingMarkdown
} from '~/components/chat/streamingMarkdown'
import { Modal } from '~/components/modals/CurieModal'
import { isLandingCoderTask } from '~/components/task/LandingCoderTaskPanel'
import { isLandingDataWeaveTask } from '~/components/task/LandingDataWeaveTaskPanel'
import { isLandingDocumentationTask } from '~/components/task/LandingDocumentationTaskPanel'
import { TaskBubble } from '~/components/task/TaskBubble'
import { TaskStatuses } from '~/constants/task-status'
import { useCurrentConversation } from '~/contexts/CurrentConversationContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { AgentTaskStatus, agentTaskStatusFromJSON } from '~/protos/agents'
import type { Diff, TaskStatusAndRequestResponseTuple } from '~/protos/agents'
import FollowUpActionIcon from '~/assets/follow-up-action.svg'
import { bridge } from '~/services/bridge'
import { createTextToSpeechAudio } from '~/services/speechService'
import { applyDiff } from '~/services/taskService'
import type { ConversationMetadata } from '~/types/Conversations'
import type { TaskObject, TaskObjectWithNoDetails } from '~/types/TaskType'
import { TabOption } from '~/types/TabOption'
import { cm, fetchWrapper } from '~/utils'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { getEffectiveTaskStatus, normalizeCommandType } from '~/task/commandTypeUtils'
import { generateTaskTitle } from '~/utils/taskTitles'
import { BetterToast } from '~/utils/toast'

function getDisplayTaskStatus(task: TaskObjectWithNoDetails | undefined) {
  if (!task) return undefined
  const status = agentTaskStatusFromJSON(task.status)
  return getEffectiveTaskStatus(status, normalizeCommandType(task.cmd_type))
}

function getTaskStatusGroup(task: TaskObjectWithNoDetails | undefined) {
  const status = getDisplayTaskStatus(task)
  if (status === undefined) return 'Task'
  return TaskStatuses[status]?.group
}

function formatFallbackTaskStatus(status: unknown): string {
  return typeof status === 'string' && status.trim()
    ? status.replaceAll('_', ' ').toLowerCase()
    : 'Task'
}

function getTaskStatusLabel(task: TaskObjectWithNoDetails | undefined): string {
  if (!task) return 'Task'
  const status = getDisplayTaskStatus(task)
  if (status === undefined) {
    return formatFallbackTaskStatus(task.status)
  }
  const config = TaskStatuses[status]
  if (config?.label) return config.label
  switch (config?.group) {
    case 'IN_PROGRESS':
      return 'Running'
    case 'NEED_ACTION':
      return 'Ready for review'
    case 'COMPLETED':
      return 'Completed'
    case 'ERRORED':
      return 'Errored'
    case 'STOPPED':
      return 'Stopped'
    default:
      return formatFallbackTaskStatus(task.status)
  }
}

function isTaskRunning(task: TaskObjectWithNoDetails | undefined): boolean {
  return getTaskStatusGroup(task) === 'IN_PROGRESS'
}

function isTaskDiffGenerated(task: TaskObjectWithNoDetails | undefined): boolean {
  return !!task && agentTaskStatusFromJSON(task.status) === AgentTaskStatus.DIFF_GENERATED
}

function isGeneratedCoderTask(task: TaskObjectWithNoDetails | undefined): boolean {
  if (!task || !isLandingCoderTask(task)) return false
  const status = agentTaskStatusFromJSON(task.status)
  return status === AgentTaskStatus.DIFF_GENERATED || status === AgentTaskStatus.COMPLETED
}

function shouldHideGeneratedCoderCardDuringLoading(
  task: TaskObjectWithNoDetails | undefined
): boolean {
  return isGeneratedCoderTask(task)
}

const PULL_REQUEST_REPO_CONN_TYPES = new Set([
  'github',
  'github_custom',
  'bitbucket',
  'azure',
  'azure_devops',
  'azuredevops',
  'gitlab',
  'gitlab_custom'
])

function normalizeRepoConnType(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replaceAll('-', '_')
}

function isPullRequestRepoConnType(value: string | null | undefined): boolean {
  return PULL_REQUEST_REPO_CONN_TYPES.has(normalizeRepoConnType(value))
}

function hasRepositoryName(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

function isPullRequestCapableConversation(metadata: ConversationMetadata | undefined): boolean {
  if (!metadata) return false

  if (hasRepositoryName(metadata.repository) && isPullRequestRepoConnType(metadata.conn_type)) {
    return true
  }

  if (
    (metadata.repositories ?? []).some(hasRepositoryName) &&
    isPullRequestRepoConnType(metadata.conn_type)
  ) {
    return true
  }

  return (metadata.repository_branches ?? []).some(
    branch => hasRepositoryName(branch.repository) && isPullRequestRepoConnType(branch.conn_type)
  )
}

function extractLandingCoderDiffsFromTuple(
  tuple: TaskStatusAndRequestResponseTuple
): Diff[] | null {
  const typedTuple = tuple as {
    request?: { diff?: Diff[] }
  } & TaskStatusAndRequestResponseTuple
  return typedTuple.response?.[0]?.responseContentStream?.diff || typedTuple.request?.diff || null
}

function findLatestLandingCoderDiffs(task: TaskObject | undefined): Diff[] | null {
  const tuples = (task?.details?.orderedStateResponses ?? []) as TaskStatusAndRequestResponseTuple[]

  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const tuple = tuples[i]
    const diffs = extractLandingCoderDiffsFromTuple(tuple)
    if (diffs?.length) return diffs
  }

  return null
}

function getTaskTimestampMs(value: string | undefined): number | undefined {
  if (!value) return undefined
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function getTaskArtifactTitle(task: TaskObjectWithNoDetails | undefined): string {
  if (!task) return 'Task'
  return generateTaskTitle(normalizeCommandType(task.cmd_type))
}

const responseSpeechTerminateEvent = 'response-speech-terminate'

// ─────────────────────────────────────────────────────────────────────────
// INVARIANT (FRAGILE): SpeechCoordinator is a process-wide singleton.
// ─────────────────────────────────────────────────────────────────────────
// Why a singleton (and not a React context):
// - Speech audio is a global browser resource; we cap concurrent active
//   playbacks at `MAX_ACTIVE_SCOPES` across the entire app (any view, any
//   conversation). A context scoped to <ChatDisplay> would leak past
//   route changes that swap the provider tree and would not coordinate
//   between sibling chat surfaces.
// - The active-scope list is HARD BOUNDED to `MAX_ACTIVE_SCOPES` at every
//   `activate()`, so the array can never grow unbounded even if a
//   ChatMessage's unmount cleanup fails to fire.
//
// Why this is still safe across React unmounts:
// - Each ChatMessage's effect cleanup calls `coordinator.clear(scope)`
//   on unmount AND when its `speechPlaybackScope` changes (conversation
//   or message id swap). `clear()` is idempotent.
//
// Test seam: `__resetSpeechCoordinatorForTesting()` resets the singleton
// between tests so module state never leaks across vitest test files.
// Production code MUST NOT call this.
//
// If you change this design (e.g., move to context), preserve the global
// concurrency cap: speech that overlaps across chat surfaces is jarring.
// ─────────────────────────────────────────────────────────────────────────
class SpeechCoordinator {
  private static readonly MAX_ACTIVE_SCOPES = 3
  private readonly events = new EventTarget()
  private activeScopes: string[] = []

  activate(scope: string): void {
    this.activeScopes = this.activeScopes.filter(s => s !== scope)
    this.activeScopes.push(scope)

    const overflow = Math.max(0, this.activeScopes.length - SpeechCoordinator.MAX_ACTIVE_SCOPES)
    const scopesToTerminate = this.activeScopes.slice(0, overflow)
    this.activeScopes = this.activeScopes.slice(-SpeechCoordinator.MAX_ACTIVE_SCOPES)

    for (const scopeToTerminate of scopesToTerminate) {
      this.events.dispatchEvent(
        new CustomEvent(responseSpeechTerminateEvent, { detail: { scope: scopeToTerminate } })
      )
    }
  }

  clear(scope: string): void {
    this.activeScopes = this.activeScopes.filter(s => s !== scope)
  }

  subscribe(handler: (event: Event) => void): () => void {
    this.events.addEventListener(responseSpeechTerminateEvent, handler)
    return () => this.events.removeEventListener(responseSpeechTerminateEvent, handler)
  }

  /** Test-only. Resets the singleton between vitest files. */
  __resetForTesting(): void {
    this.activeScopes = []
  }
}

const responseSpeechCoordinator = new SpeechCoordinator()

/** Test-only helper. Do NOT call from production code. */
export function __resetSpeechCoordinatorForTesting(): void {
  responseSpeechCoordinator.__resetForTesting()
}

function isAbortError(error: unknown) {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  )
}

export interface ChatMessageProps {
  message: ChatMessage
  onTaskLinkClick?: (taskId: number) => void
  onTaskPanelOpen?: (taskId: number) => void
  onFeedbackUpdate?: (
    messageId: string,
    feedback: 'positive' | 'negative' | undefined,
    feedbackReason?: string,
    otherReason?: string
  ) => void
  onEditableCanvasClick?: (canvas: CanvasItem) => void // Handler for editable canvas click
  onSelectionWidgetChange?: (widget: ChatSelectionWidget, selectedValues: string[]) => void
  selectionWidgetSelections?: Record<string, string[]>
  isSelectionWidgetDisabled?: boolean
  isLoading?: boolean // Indicates if streaming is active
  isInGracePeriod?: boolean // Indicates if we're in grace period after streaming stops
  isTaskInMessages?: boolean // Indicates if this task appears in the current messages array
  isLastProgressMessage?: boolean // Indicates if this is the last progress_message in the list
  feedbackMessage?: ChatMessage // The text message to use for feedback (precomputed by parent)
  allMessages?: ChatMessage[] // All messages (for finding progress text for task messages)
  messageIndex?: number // Index of this message in allMessages
  isSplitPaneLayout?: boolean
  hiddenTaskKickoffId?: number | null
  onStreamingRevealStateChange?: (
    messageId: string,
    isRevealing: boolean,
    rawPrefixLength: number
  ) => void
  onCoderTaskCodeReview?: (taskId: number) => void
  onCoderTaskFeedbackDraft?: (taskId: number) => void
}

// Streaming markdown buffering/stabilization lives in
// `components/markdown/streamingMarkdown.ts`; keep ChatDisplay as a consumer.

const GENERIC_ASSISTANT_ERROR_PREFIX = 'We encountered an issue processing your request.'
const GENERATED_DETAILS_MARKER_PATTERN = /Here's\s+(?:what's being|what it will)\s+generate(?:d)?:/i

function splitFirstMarkdownParagraph(content: string): {
  firstParagraph: string
  remaining: string
} {
  const trimmed = content.trim()
  if (!trimmed) {
    return { firstParagraph: '', remaining: '' }
  }

  const [firstParagraph, ...rest] = trimmed.split(/\n\s*\n/)
  return {
    firstParagraph: firstParagraph.trim(),
    remaining: rest.join('\n\n').trim()
  }
}

function extractTaskIdFromKickoff(content: string): number | undefined {
  const match = content.match(/Task ID:\s*(\d+)/i)
  return match ? Number(match[1]) : undefined
}

function extractGeneratedDetails(content: string): string | undefined {
  const markerMatch = GENERATED_DETAILS_MARKER_PATTERN.exec(content)
  if (!markerMatch) return undefined

  const afterMarker = content.slice(markerMatch.index + markerMatch[0].length)
  const endMatch = afterMarker.match(/\n\n(?:The progress|Would you like)/)
  const tableContent = (endMatch ? afterMarker.slice(0, endMatch.index) : afterMarker).trim()

  if (!tableContent) return undefined
  return `**What's Being Generated**\n\n${tableContent}`
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function findSummarySectionStart(
  content: string,
  sectionNames: string[],
  fromIndex = 0
): number | undefined {
  let firstIndex: number | undefined

  for (const sectionName of sectionNames) {
    const pattern = new RegExp(
      `(^|\\n)\\s*(?:\\*\\*)?${escapeRegex(sectionName)}(?:\\*\\*)?:?`,
      'i'
    )
    const match = pattern.exec(content.slice(fromIndex))
    if (!match) continue

    const relativeIndex = match.index + (match[0].startsWith('\n') ? 1 : 0)
    const absoluteIndex = fromIndex + relativeIndex
    firstIndex = firstIndex === undefined ? absoluteIndex : Math.min(firstIndex, absoluteIndex)
  }

  return firstIndex
}

function splitSummaryDetails(remaining: string): {
  visibleDetails: string
  collapsibleDetails: string
} {
  const changesStart = findSummarySectionStart(remaining, ['Changes'])
  if (changesStart === undefined) {
    return { visibleDetails: remaining, collapsibleDetails: '' }
  }

  const visibleSectionStart = findSummarySectionStart(
    remaining,
    ['Key Decisions & Assumptions', 'Verification', 'Next Steps'],
    changesStart + 1
  )

  if (visibleSectionStart === undefined) {
    return {
      visibleDetails: remaining.slice(0, changesStart).trim(),
      collapsibleDetails: remaining.slice(changesStart).trim()
    }
  }

  return {
    visibleDetails: `${remaining.slice(0, changesStart).trim()}\n\n${remaining
      .slice(visibleSectionStart)
      .trim()}`.trim(),
    collapsibleDetails: remaining.slice(changesStart, visibleSectionStart).trim()
  }
}

function hasLaterCoderSummaryForTask(
  messages: ChatMessage[],
  messageIndex: number,
  taskId: number | undefined
): boolean {
  if (!taskId || messageIndex < 0) return false
  return messages.some(
    (candidate, idx) =>
      idx > messageIndex && candidate.type === 'coder_summary' && candidate.taskId === taskId
  )
}

function findGeneratedDetailsForSummary(
  messages: ChatMessage[],
  summaryIndex: number,
  taskId: number | undefined
): string | undefined {
  if (!taskId || summaryIndex < 0) return undefined

  for (let i = summaryIndex - 1; i >= 0; i -= 1) {
    const candidate = messages[i]
    if (candidate.role === 'user') break
    if (candidate.type !== 'text') continue
    if (extractTaskIdFromKickoff(candidate.content) !== taskId) continue

    return extractGeneratedDetails(candidate.content)
  }

  return undefined
}

export function findTaskKickoffContent(
  messages: ChatMessage[],
  taskId: number | undefined
): string | undefined {
  if (!taskId) return undefined

  for (const message of messages) {
    if (message.role === 'user' || message.type !== 'text') continue
    if (extractTaskIdFromKickoff(message.content) !== taskId) continue
    if (!extractGeneratedDetails(message.content)) continue

    return message.content
  }

  return undefined
}

function getSelectedValuesForWidget(
  widget: ChatSelectionWidget,
  selections: Record<string, string[]>
): string[] {
  if (Object.prototype.hasOwnProperty.call(selections, widget.widgetId)) {
    return selections[widget.widgetId] ?? []
  }
  return widget.defaultSelectedValues ?? []
}

export function ChatMessage({
  message,
  onTaskLinkClick,
  onTaskPanelOpen,
  onFeedbackUpdate,
  onEditableCanvasClick,
  onSelectionWidgetChange,
  selectionWidgetSelections = {},
  isSelectionWidgetDisabled = false,
  isLoading,
  isInGracePeriod = false,
  isTaskInMessages = false,
  isLastProgressMessage = false,
  feedbackMessage,
  allMessages = [],
  messageIndex = -1,
  isSplitPaneLayout = false,
  hiddenTaskKickoffId,
  onStreamingRevealStateChange,
  onCoderTaskCodeReview,
  onCoderTaskFeedbackDraft
}: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isCompact = bridge.isInPlugin()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [canvasViewerOpen, setCanvasViewerOpen] = useState(false)
  const [selectedCanvasIndex, setSelectedCanvasIndex] = useState(0)
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
  const [isRaisePrPending, setIsRaisePrPending] = useState(false)
  const { conversation, isLoading: isConversationLoading } = useCurrentConversation()
  const { currentWorkspace } = useWorkspaceContext()
  const [isSpeechLoading, setIsSpeechLoading] = useState(false)
  const [isSpeechPlaying, setIsSpeechPlaying] = useState(false)
  const [isSpeechPaused, setIsSpeechPaused] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const speechAudioCleanupRef = useRef<(() => void) | null>(null)
  const speechRequestControllerRef = useRef<AbortController | null>(null)
  const speechPlaybackScopeRef = useRef<string | null>(null)
  const assistantTextContainerRef = useRef<HTMLDivElement | null>(null)
  const speechSourceMessage =
    message.type === 'text' ? message : feedbackMessage?.type === 'text' ? feedbackMessage : null

  // CUR-206: with interleaved progress + text, a turn can contain multiple
  // assistant-text segments (think -> text -> think -> text). The TTS
  // button applies to the WHOLE TURN, so we read all segments from the
  // shared selector in `chat/design/chatClusterSelectors.ts`. Defensive
  // fallback (if `allMessages` is missing/stale) keeps single-message TTS
  // working.
  const turnTextSegmentsForSpeech = useMemo<ChatMessage[]>(() => {
    if (!speechSourceMessage || speechSourceMessage.role === 'user') return []
    if (messageIndex < 0 || allMessages.length === 0) {
      return [speechSourceMessage]
    }
    const segments = getTurnTextSegments(allMessages, messageIndex)
    if (!segments.some(segment => segment.id === speechSourceMessage.id)) {
      return [speechSourceMessage]
    }
    return segments
  }, [allMessages, messageIndex, speechSourceMessage])

  // We test against `turnTextSegmentsForSpeech` (the SELECTOR-computed turn
  // segments) rather than calling `isLastTextOfTurn(allMessages,
  // messageIndex)` directly, because the current message at `messageIndex`
  // may be a non-text entry (canvas, PMV) that CARRIES the feedback (and
  // therefore the TTS control) on behalf of an earlier text segment. The
  // `isLastTextOfTurn` selector is keyed off the index pointing at the
  // text segment itself, which doesn't match here. Guarded by:
  // `renders response audio after a following canvas message`.
  const isLastTextOfTurnForSpeech = useMemo(() => {
    if (!speechSourceMessage) return false
    if (turnTextSegmentsForSpeech.length === 0) return false
    return (
      turnTextSegmentsForSpeech[turnTextSegmentsForSpeech.length - 1].id === speechSourceMessage.id
    )
  }, [speechSourceMessage, turnTextSegmentsForSpeech])

  const speechPlaybackScope = `${conversation?.uuid ?? ''}:${speechSourceMessage?.id ?? ''}`
  const hasSpeechAudio = Boolean(audioRef.current)

  // Smooth streaming buffer: reveals text at a steady rate instead of
  // dumping chunks as they arrive from the server.
  const rawContent = message.type === 'text' ? message.content : ''
  const debugTextDeltas = message.type === 'text' ? message.textDeltas : undefined
  // CUR-206: speech playback uses the concatenated text of ALL segments in the
  // turn (think -> text -> think -> text), not just the source segment. This
  // is what the user "reads" as one assistant response. Two newlines between
  // segments give the TTS engine a natural pause boundary.
  const speechSourceContent = useMemo(() => {
    if (turnTextSegmentsForSpeech.length === 0) {
      return speechSourceMessage?.type === 'text' ? speechSourceMessage.content : ''
    }
    return turnTextSegmentsForSpeech
      .map(segment => (segment.type === 'text' ? segment.content : ''))
      .filter(content => content.length > 0)
      .join('\n\n')
  }, [speechSourceMessage, turnTextSegmentsForSpeech])
  const speechContent = useMemo(() => stripMarkdown(speechSourceContent), [speechSourceContent])
  const isGenericAssistantError = rawContent.startsWith(GENERIC_ASSISTANT_ERROR_PREFIX)
  const isSpeechSourceGenericAssistantError = speechSourceContent.startsWith(
    GENERIC_ASSISTANT_ERROR_PREFIX
  )
  const isActivelyStreaming =
    !isUser &&
    message.type === 'text' &&
    !isGenericAssistantError &&
    ((!!isLoading && !('showFeedback' in message && message.showFeedback)) ||
      !!message.continueStreamingAnimation)
  const streamingRevealCacheKey =
    !isUser && message.type === 'text'
      ? `${conversation?.uuid ?? 'conversation'}:assistant:${
          message.textDeltas?.[0]?.receivedAtMs ?? message.createdTime ?? message.id
        }`
      : undefined
  const {
    content: smoothContent,
    rawPrefixLength: smoothRawPrefixLength,
    isComplete: isStreamingTextComplete
  } = useStreamingMarkdown(rawContent, isActivelyStreaming, streamingRevealCacheKey)
  const isVisuallyStreaming = isActivelyStreaming && !isStreamingTextComplete
  const shouldShowAssistantFeedback = !!feedbackMessage && !isVisuallyStreaming

  useEffect(() => {
    if (message.type !== 'text' || isUser) return
    onStreamingRevealStateChange?.(message.id, isVisuallyStreaming, smoothRawPrefixLength)
  }, [
    isUser,
    isVisuallyStreaming,
    message.id,
    message.type,
    onStreamingRevealStateChange,
    smoothRawPrefixLength
  ])

  // CUR-206: `shouldMoveSpeechActionToCanvas` was removed alongside the
  // standalone-TTS fallback in the text branch render. The feedback (with
  // its embedded TTS button) now always sits on the cluster's last entry,
  // which naturally puts it after canvas attachments without a special
  // case.
  const canPlaySpeech =
    speechSourceMessage?.role !== 'user' &&
    speechSourceMessage?.type === 'text' &&
    !isVisuallyStreaming &&
    !isSpeechSourceGenericAssistantError &&
    speechContent.trim().length > 0 &&
    // CUR-206: only the LAST text segment of a turn renders the TTS control.
    // Earlier segments are mid-turn fragments — showing the button there would
    // place it visually above the next thinking/text bubble and play back only
    // half the response. The button on the last segment plays the whole turn
    // (via `speechSourceContent` above).
    isLastTextOfTurnForSpeech &&
    // CUR-206: even on the last-so-far text segment, suppress the button while
    // the turn is still streaming. With interleaved phases, what looks like
    // "the last text" mid-stream may have another think -> text phase coming
    // right after, and we'd flash a TTS button between segments. `isLoading`
    // is plumbed from ChatDisplay as `isLoading && isLatestTurnMessage`, so
    // it's true ONLY for the in-flight turn — completed prior turns still
    // render the button as before.
    !isLoading
  const progressTaskContext = useMemo(() => {
    if (message.type !== 'progress_message') return undefined

    if (messageIndex >= 0 && allMessages.length > 0) {
      for (let i = messageIndex + 1; i < allMessages.length; i += 1) {
        const nextMsg = allMessages[i]
        if (nextMsg.role === 'user') break
        if (nextMsg.type === 'task' && nextMsg.taskId) {
          const task = conversation?.tasks?.find(candidate => candidate?.task_id === nextMsg.taskId)
          return {
            taskId: nextMsg.taskId,
            taskTitle: nextMsg.taskTitle ?? getTaskArtifactTitle(task),
            statusLabel: getTaskStatusLabel(task),
            isRunning: task ? isTaskRunning(task) : !!isLoading,
            createdTsMs: getTaskTimestampMs(task?.created_ts),
            updatedTsMs: getTaskTimestampMs(task?.updated_ts)
          }
        }
      }
    }

    const runningLandingTasks = (conversation?.tasks ?? []).filter(
      task =>
        (isLandingCoderTask(task) ||
          isLandingDataWeaveTask(task) ||
          isLandingDocumentationTask(task)) &&
        isTaskRunning(task)
    )

    if (runningLandingTasks.length !== 1) return undefined
    const task = runningLandingTasks[0]
    return {
      taskId: task.task_id,
      taskTitle: getTaskArtifactTitle(task),
      statusLabel: getTaskStatusLabel(task),
      isRunning: true,
      createdTsMs: getTaskTimestampMs(task.created_ts),
      updatedTsMs: getTaskTimestampMs(task.updated_ts)
    }
  }, [allMessages, conversation?.tasks, isLoading, message.type, messageIndex])

  useEffect(() => {
    if (!isStreamingMarkdownDebugEnabled()) return
    if (message.type !== 'text' || isUser) return
    /*console.warn('[chat-md-debug-message]', {
      messageId: message.id,
      ...buildStreamingMarkdownDeltaDebug(rawContent, debugTextDeltas)
    })*/
  }, [message.id, message.type, isUser, rawContent, debugTextDeltas])

  useEffect(() => {
    if (!isActivelyStreaming) return
    if (!isStreamingMarkdownDebugEnabled()) return

    const rawOddRows = getOddDoubleAsteriskTableRows(rawContent)
    const smoothOddRows = getOddDoubleAsteriskTableRows(smoothContent)
    const renderedText = assistantTextContainerRef.current?.textContent ?? ''
    const rawMarkdownMarkerLocations = getRawMarkdownMarkerLocations(
      assistantTextContainerRef.current
    )

    // Debug probe for user-reported table/body flicker:
    // compares raw stream, stabilized stream, and what actually made it into
    // the DOM text for this frame.
    console.warn('[chat-md-debug]', {
      messageId: message.id,
      rawLength: rawContent.length,
      smoothLength: smoothContent.length,
      smoothRawPrefixLength,
      smoothBoundary: getTextDeltaBoundary(smoothRawPrefixLength, debugTextDeltas),
      rawOddDoubleAsteriskTableRows: rawOddRows,
      smoothOddDoubleAsteriskTableRows: smoothOddRows,
      renderedHasRawDoubleAsterisk: renderedText.includes('**'),
      rawMarkdownMarkerLocations,
      rawMarkdownMarkersOutsideTables: rawMarkdownMarkerLocations.filter(
        location => !location.insideTable
      ),
      rawTail: rawContent.slice(-200),
      smoothTail: smoothContent.slice(-200),
      renderedTail: renderedText.slice(-200)
    })
  }, [
    isActivelyStreaming,
    rawContent,
    smoothContent,
    smoothRawPrefixLength,
    message.id,
    debugTextDeltas
  ])

  const releaseSpeechAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.onended = null
      audioRef.current.onerror = null
      audioRef.current.onplaying = null
      audioRef.current = null
    }
    speechAudioCleanupRef.current?.()
    speechAudioCleanupRef.current = null
  }, [])

  const stopSpeechAudio = useCallback(() => {
    speechRequestControllerRef.current?.abort()
    speechRequestControllerRef.current = null
    releaseSpeechAudio()
    responseSpeechCoordinator.clear(speechPlaybackScope)
    setIsSpeechPlaying(false)
    setIsSpeechPaused(false)
    setIsSpeechLoading(false)
  }, [releaseSpeechAudio, speechPlaybackScope])

  useEffect(() => {
    return () => {
      speechRequestControllerRef.current?.abort()
      speechRequestControllerRef.current = null
      releaseSpeechAudio()
      responseSpeechCoordinator.clear(speechPlaybackScope)
    }
  }, [releaseSpeechAudio, speechPlaybackScope])

  useEffect(() => {
    if (speechPlaybackScopeRef.current === null) {
      speechPlaybackScopeRef.current = speechPlaybackScope
      return
    }

    if (speechPlaybackScopeRef.current !== speechPlaybackScope) {
      speechPlaybackScopeRef.current = speechPlaybackScope
      stopSpeechAudio()
    }
  }, [speechPlaybackScope, stopSpeechAudio])

  useEffect(() => {
    if (!canPlaySpeech) {
      stopSpeechAudio()
    }
  }, [canPlaySpeech, stopSpeechAudio])

  useEffect(() => {
    const handleSpeechTerminate = (event: Event) => {
      const scopeToTerminate = (event as CustomEvent<{ scope: string }>).detail?.scope
      if (scopeToTerminate === speechPlaybackScope) {
        stopSpeechAudio()
      }
    }

    return responseSpeechCoordinator.subscribe(handleSpeechTerminate)
  }, [speechPlaybackScope, stopSpeechAudio])

  const handlePauseSpeech = useCallback(() => {
    if (!audioRef.current) return

    audioRef.current.pause()
    setIsSpeechPlaying(false)
    setIsSpeechPaused(true)
    setIsSpeechLoading(false)
  }, [])

  const handleSpeakResponse = useCallback(async () => {
    if (!canPlaySpeech || isSpeechLoading) return

    if (!currentWorkspace?.uuid) {
      BetterToast.error('No workspace selected')
      return
    }

    if (audioRef.current && isSpeechPaused) {
      responseSpeechCoordinator.activate(speechPlaybackScope)
      setIsSpeechLoading(true)
      setIsSpeechPaused(false)
      try {
        await audioRef.current.play()
        setIsSpeechPlaying(true)
      } catch (error) {
        setIsSpeechPaused(true)
        BetterToast.error(error instanceof Error ? error.message : 'Speech playback failed')
      } finally {
        setIsSpeechLoading(false)
      }
      return
    }

    setIsSpeechLoading(true)
    responseSpeechCoordinator.activate(speechPlaybackScope)
    const speechRequestController = new AbortController()
    speechRequestControllerRef.current?.abort()
    speechRequestControllerRef.current = speechRequestController

    try {
      const speechAudio = await createTextToSpeechAudio(
        currentWorkspace.uuid,
        speechContent.trim(),
        {
          signal: speechRequestController.signal
        }
      )
      if (
        speechRequestController.signal.aborted ||
        speechRequestControllerRef.current !== speechRequestController
      ) {
        speechAudio.cleanup()
        return
      }

      releaseSpeechAudio()
      const audio = speechAudio.audio
      speechAudioCleanupRef.current = speechAudio.cleanup
      audioRef.current = audio

      audio.onended = () => {
        speechRequestControllerRef.current = null
        releaseSpeechAudio()
        responseSpeechCoordinator.clear(speechPlaybackScope)
        setIsSpeechPlaying(false)
        setIsSpeechPaused(false)
        setIsSpeechLoading(false)
      }
      audio.onerror = () => {
        speechRequestControllerRef.current = null
        releaseSpeechAudio()
        responseSpeechCoordinator.clear(speechPlaybackScope)
        setIsSpeechPlaying(false)
        setIsSpeechPaused(false)
        setIsSpeechLoading(false)
        BetterToast.error('Failed to play generated audio')
      }
      audio.onplaying = () => {
        if (speechRequestControllerRef.current === speechRequestController) {
          setIsSpeechLoading(false)
          setIsSpeechPlaying(true)
          setIsSpeechPaused(false)
        }
      }

      speechAudio.done
        .catch(error => {
          if (
            speechRequestController.signal.aborted ||
            speechRequestControllerRef.current !== speechRequestController ||
            isAbortError(error)
          ) {
            return
          }
          releaseSpeechAudio()
          responseSpeechCoordinator.clear(speechPlaybackScope)
          setIsSpeechPlaying(false)
          setIsSpeechPaused(false)
          setIsSpeechLoading(false)
          BetterToast.error(error instanceof Error ? error.message : 'Speech synthesis failed')
        })
        .finally(() => {
          if (
            speechRequestControllerRef.current === speechRequestController &&
            audioRef.current !== audio
          ) {
            speechRequestControllerRef.current = null
          }
        })

      await audio.play()
      if (speechRequestControllerRef.current === speechRequestController) {
        setIsSpeechLoading(false)
        setIsSpeechPlaying(true)
        setIsSpeechPaused(false)
      }
    } catch (error) {
      if (
        speechRequestController.signal.aborted ||
        speechRequestControllerRef.current !== speechRequestController ||
        isAbortError(error)
      ) {
        return
      }
      releaseSpeechAudio()
      responseSpeechCoordinator.clear(speechPlaybackScope)
      setIsSpeechPlaying(false)
      setIsSpeechPaused(false)
      BetterToast.error(error instanceof Error ? error.message : 'Speech synthesis failed')
    } finally {
      if (speechRequestControllerRef.current === speechRequestController && !audioRef.current) {
        speechRequestControllerRef.current = null
        setIsSpeechLoading(false)
      }
    }
  }, [
    canPlaySpeech,
    currentWorkspace?.uuid,
    isSpeechPaused,
    isSpeechLoading,
    speechPlaybackScope,
    speechContent,
    releaseSpeechAudio
  ])

  const speechActionButton = canPlaySpeech ? (
    <>
      <Button
        type='button'
        variant='borderless'
        onClick={isSpeechPlaying ? handlePauseSpeech : handleSpeakResponse}
        isDisabled={isSpeechLoading && !hasSpeechAudio}
        className={responseActionButtonClassName}
        aria-label={
          isSpeechPlaying
            ? 'Pause response audio'
            : isSpeechPaused
              ? 'Resume response audio'
              : 'Play response audio'
        }
        title={
          isSpeechPlaying
            ? 'Pause response audio'
            : isSpeechPaused
              ? 'Resume response audio'
              : 'Play response audio'
        }
      >
        {isSpeechPlaying ? (
          <Pause className='h-4 w-4' fill='currentColor' strokeWidth={0} />
        ) : isSpeechPaused ? (
          <Play className='h-4 w-4' fill='currentColor' strokeWidth={1.5} />
        ) : isSpeechLoading ? (
          <Loader2 className='h-4 w-4 animate-spin' strokeWidth={1.5} />
        ) : (
          <Volume2 className='h-4 w-4' strokeWidth={1.5} />
        )}
      </Button>
      {(isSpeechLoading || isSpeechPlaying || isSpeechPaused) && (
        <Button
          type='button'
          variant='borderless'
          onClick={stopSpeechAudio}
          className={responseActionButtonClassName}
          aria-label='Stop response audio'
          title='Stop response audio'
        >
          <Square className='h-3.5 w-3.5' fill='currentColor' />
        </Button>
      )}
    </>
  ) : null

  if (message.type === 'progress_message') {
    // Only pass isStreaming=true to the LAST progress message cluster
    // This prevents old clusters from reappearing when new streaming starts

    if (isUser) {
      console.warn('Unexpected: progress message from user', message)
    }

    const isActiveCluster = isLoading && isLastProgressMessage
    return (
      <>
        <ChatProgressMessageView
          progressMessages={message.progressMessages}
          isStreaming={isActiveCluster}
          taskContext={progressTaskContext}
        />
        {/* CUR-206: when this PMV is the LAST entry of an assistant cluster
            (`feedbackMessage` is set by computeFeedbackPositions only at the
            cluster's end index), render the response feedback (TTS + thumbs)
            here so the controls always sit at the visual bottom of the turn.
            Without this, interleaved turns ending in a PMV would push the
            controls onto a mid-turn text segment via the standalone-TTS
            fallback (which we've also removed) — appearing above the next
            thinking bubble.
            The `px-[16px]` mirrors the inset used by the text branch's
            cluster wrapper (`gap-y-7 px-[16px]`) and by the PMV chrome's
            own internal padding, so the buttons line up with the title
            above and with the conversation's other turns. Compact and
            split-pane layouts skip the inset to match the text branch's
            conditional in those modes. */}
        {shouldShowAssistantFeedback && (
          <div
            className={cm(
              'mt-2',
              // Cluster-end inset mirrors the text branch's `px-[16px]` so
              // the feedback controls land in the same column whether the
              // cluster ends in a PMV or in a text message (CUR-206).
              shouldApplyClusterEndInset({ isCompact, isSplitPaneLayout }) &&
                ASSISTANT_CLUSTER_END_INSET_CLASS
            )}
          >
            <AssistantResponseFeedback
              message={feedbackMessage}
              onFeedbackUpdate={onFeedbackUpdate}
              trailingAction={speechActionButton}
            />
          </div>
        )}
      </>
    )
  }

  if (message.type === 'task') {
    if (isUser) {
      console.warn('Unexpected: task message from user', message)
    }

    const taskId = message.taskId
    const title = message.taskTitle

    // Safeguard: Ensure we have valid taskId and title
    if (!taskId || !title) {
      return null
    }

    const hasCompletedSummary = hasLaterCoderSummaryForTask(allMessages, messageIndex, taskId)
    if (hasCompletedSummary) {
      return null
    }

    // Don't render TaskBubble until conversation data has loaded
    // This prevents showing "deleted" state for newly created tasks
    if (isConversationLoading || conversation === null) {
      return null
    }

    // Look up task status from conversation's tasks array
    // Use same conversion and transformation logic as TaskItemWrapper
    // Safeguard: Handle case where conversation or tasks array might be undefined
    const task = conversation.tasks?.find(t => t?.task_id === taskId)
    if ((isLoading || isInGracePeriod) && shouldHideGeneratedCoderCardDuringLoading(task)) {
      return null
    }

    // Task deletion logic:
    // - If streaming is active AND task is in messages → protect (not deleted)
    // - If streaming ended AND task not in conversation.tasks → deleted
    // This prevents "deleted" flash during streaming while correctly showing
    // deleted state for tasks from stale sessions
    const shouldProtectFromDeletion = isTaskInMessages && (isLoading || isInGracePeriod)
    const isTaskDeleted = !task && !shouldProtectFromDeletion

    const canOpenSidePanel =
      !!task &&
      !isTaskDeleted &&
      (isLandingCoderTask(task) ||
        isLandingDataWeaveTask(task) ||
        isLandingDocumentationTask(task)) &&
      !!onTaskPanelOpen

    const handleTaskClick = (clickedTaskId: number) => {
      // Safeguard: Don't navigate if task is deleted
      if (isTaskDeleted) {
        BetterToast.error('This task has been deleted')
        return
      }
      if (canOpenSidePanel && onTaskPanelOpen) {
        onTaskPanelOpen(clickedTaskId)
        return
      }
      if (onTaskLinkClick) {
        onTaskLinkClick(clickedTaskId)
      }
    }

    // Get progress text from the previous progress_message cluster (for streaming)
    // Look backwards from this task to find the associated progress cluster
    let streamingProgressText: string | undefined
    if (messageIndex >= 0 && allMessages.length > 0) {
      // Look at the message immediately before this task
      for (let i = messageIndex - 1; i >= 0; i--) {
        const prevMsg = allMessages[i]
        if (prevMsg.type === 'progress_message') {
          // Handle both grouped (progressMessages array) and individual (progressMessage) formats
          const progressMsgs = prevMsg.progressMessages || []

          if (progressMsgs.length > 0) {
            // First try to find a SUMMARY
            const summaryMsg = progressMsgs.find(pm => pm.messageType === 'SUMMARY')
            if (summaryMsg?.title) {
              streamingProgressText = summaryMsg.title
            } else {
              // Otherwise use the latest message title
              streamingProgressText = progressMsgs[progressMsgs.length - 1]?.title
            }
          }
          break // Found the progress cluster, stop looking
        }
        // Stop if we hit a user message (different turn)
        if (prevMsg.role === 'user') {
          break
        }
      }
    }

    // Render TaskBubble with safeguards - if task is deleted, disable interaction
    // Pass streaming progress text if available, otherwise TaskBubble will fetch from API
    return (
      <TaskBubble
        taskId={taskId}
        title={title}
        statusLabel={task && !isTaskDeleted ? getTaskStatusLabel(task) : undefined}
        statusGroup={task && !isTaskDeleted ? getTaskStatusGroup(task) : undefined}
        progressMessageText={streamingProgressText}
        conversationUuid={conversation?.uuid}
        onTaskClick={isTaskDeleted ? undefined : handleTaskClick}
        onTaskIdLinkClick={isTaskDeleted ? undefined : onTaskLinkClick}
        isDeleted={isTaskDeleted}
      />
    )
  }

  if (message.type === 'coder_summary' && !isUser) {
    const taskId = message.taskId
    const task = taskId ? conversation?.tasks?.find(t => t?.task_id === taskId) : undefined
    const title = task
      ? getTaskArtifactTitle(task)
      : message.taskTitle
        ? generateTaskTitle(normalizeCommandType(message.taskTitle))
        : 'Repository Coder'
    const canOpenSidePanel =
      !!taskId &&
      !!task &&
      (isLandingCoderTask(task) ||
        isLandingDataWeaveTask(task) ||
        isLandingDocumentationTask(task)) &&
      !!onTaskPanelOpen

    const handleOpenTask = () => {
      if (!taskId) return
      if (canOpenSidePanel && onTaskPanelOpen) {
        onTaskPanelOpen(taskId)
        return
      }
      onTaskLinkClick?.(taskId)
    }
    const handleTaskIdLinkClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (!taskId) return
      onTaskLinkClick?.(taskId)
    }
    const { firstParagraph, remaining } = splitFirstMarkdownParagraph(message.content)
    const summaryPreview = firstParagraph
      .replace(/^\*\*Summary\*\*:?\s*/i, '')
      .replace(/^Summary:\s*/i, '')
      .trim()
    const generatedDetails = findGeneratedDetailsForSummary(allMessages, messageIndex, taskId)
    const { visibleDetails, collapsibleDetails } = splitSummaryDetails(remaining)
    const expandedDetails = [generatedDetails, collapsibleDetails].filter(Boolean).join('\n\n')
    const hasMoreDetails = expandedDetails.length > 0
    const showCoderFollowUpActions =
      !!taskId && !!task && isLandingCoderTask(task) && isTaskDiffGenerated(task)
    const showRaisePrAction =
      showCoderFollowUpActions &&
      isPullRequestCapableConversation(conversation?.conversation_metadata)

    const handleCodeReviewClick = () => {
      if (taskId == null) return
      onCoderTaskCodeReview?.(taskId)
    }

    const handleFeedbackDraftClick = () => {
      if (taskId == null) return
      onCoderTaskFeedbackDraft?.(taskId)
    }

    const handleRaisePrClick = async () => {
      if (taskId == null || isRaisePrPending) return

      if (!currentWorkspace?.uuid) {
        BetterToast.error('Workspace not found')
        return
      }

      if (!conversation?.uuid) {
        BetterToast.error('Conversation not found')
        return
      }

      setIsRaisePrPending(true)
      try {
        const taskDetails = await fetchWrapper.get<{ task: TaskObject }>(
          `/api/workspace/${currentWorkspace.uuid}/conversation/${conversation.uuid}/task/${taskId}`
        )
        const taskWithDetails = taskDetails.task
        const diffs = findLatestLandingCoderDiffs(taskWithDetails)

        if (!diffs?.length) {
          BetterToast.error('No generated diff found for this task')
          return
        }

        await applyDiff({
          workspaceUuid: currentWorkspace.uuid,
          uuid: conversation.uuid,
          taskId,
          diffs,
          commandType: normalizeCommandType(taskWithDetails.cmd_type ?? task?.cmd_type)
        })
        BetterToast.success('Diff applied')
        globalEventEmitter.emit(AppEvents.setActiveTab, TabOption.HELP)
      } catch (error) {
        console.error('Error applying diff from coder summary:', error)
        BetterToast.error('Failed to apply diff')
      } finally {
        setIsRaisePrPending(false)
      }
    }

    return (
      <div className='flex w-full min-w-0 flex-col items-start'>
        <div
          className='w-[calc(100%_-_32px)] min-w-0 overflow-hidden rounded-[24px] border border-white/60 bg-tokens-neutral-bg-white/50 backdrop-blur-2xl backdrop-saturate-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_2px_0_rgba(15,15,15,0.04),0_8px_24px_-12px_rgba(15,15,15,0.08)]'
          data-testid='coder-summary-card'
        >
          <div className='flex items-center justify-center rounded-t-[24px] border-b border-white/40 bg-tokens-neutral-bg-white/60 px-4 py-2'>
            <div className='flex min-w-0 flex-1 items-center justify-between'>
              <div className='flex min-w-0 items-center gap-2'>
                <div className='flex shrink-0 items-center p-1'>
                  <div className='flex items-center rounded-lg bg-tokens-neutral-fill-fill-4 p-1 text-tokens-status-link-3'>
                    <div className='flex size-6 items-center justify-center'>
                      <CheckCircle2 className='size-[18px]' />
                    </div>
                  </div>
                </div>
                <div className='flex min-w-0 flex-col items-start'>
                  {taskId && onTaskLinkClick ? (
                    <button
                      type='button'
                      onClick={handleTaskIdLinkClick}
                      className='w-fit max-w-full cursor-pointer rounded-[4px] border-0 bg-transparent p-0 text-left text-tokens-status-link-3 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-2 focus-visible:ring-offset-2'
                      aria-label={`Open task #${taskId}`}
                    >
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelSm'
                        fontWeight='semibold'
                        className='truncate text-inherit'
                      >
                        #{taskId}
                      </Typography>
                    </button>
                  ) : (
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelSm'
                      fontWeight='semibold'
                      className='truncate text-tokens-neutral-text-tertiary'
                    >
                      {taskId ? `#${taskId}` : '#'}
                    </Typography>
                  )}
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMdMedium'
                    fontWeight='semibold'
                    className='truncate text-tokens-neutral-text-primary'
                  >
                    {title}
                  </Typography>
                </div>
              </div>
              {taskId && (
                <button
                  type='button'
                  onClick={handleOpenTask}
                  className='flex h-9 shrink-0 items-center justify-center gap-2 rounded-[12px] px-4 py-2 text-tokens-status-link-3 transition-colors hover:bg-tokens-neutral-fill-fill-4'
                  aria-label='View task results'
                >
                  <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium'>
                    View Results
                  </Typography>
                  <ChevronRight className='size-4' />
                </button>
              )}
            </div>
          </div>
          <div className='flex flex-col gap-4 px-9 py-4'>
            <div className='flex w-full flex-col'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMdMedium'
                fontWeight='bold'
                className='w-full text-tokens-neutral-text-secondary'
                style={{ lineHeight: '180%' }}
              >
                Summary
              </Typography>
              <div className='w-full text-[15px] font-medium leading-[180%] text-tokens-neutral-text-secondary'>
                <ChatMarkdown
                  className='w-full break-words text-[15px] font-medium leading-[180%] text-tokens-neutral-text-secondary [&>p]:!m-0 [&>ul]:!m-0 [&_p]:!font-medium [&_p]:!leading-[180%] [&_strong]:!font-bold [&_strong]:!text-tokens-neutral-text-secondary'
                  onTaskLinkClick={onTaskLinkClick}
                >
                  {summaryPreview}
                </ChatMarkdown>
                {hasMoreDetails && (
                  <>
                    {' '}
                    <button
                      type='button'
                      onClick={() => setIsSummaryExpanded(expanded => !expanded)}
                      className='inline-flex select-none items-center align-baseline text-[12px] font-bold leading-[180%] text-tokens-status-link-3 transition-colors hover:text-tokens-status-link-2'
                    >
                      {isSummaryExpanded ? 'Less Details' : 'More Details'}
                    </button>
                  </>
                )}
              </div>
            </div>
            {isSummaryExpanded && expandedDetails && (
              <Typography
                fontFamily='geist'
                fontSize='bodySmMedium'
                fontWeight='medium'
                className='w-full text-tokens-neutral-text-secondary'
              >
                <ChatMarkdown
                  className='w-full break-words text-[14px] font-medium leading-[180%] text-tokens-neutral-text-secondary [&>p]:!m-0 [&>ul]:!m-0 [&>p+ul]:!mt-0 [&>ul+p]:!mt-4 [&_li]:!py-0 [&_li]:!font-medium [&_li]:!leading-[180%] [&_p]:!font-medium [&_p]:!leading-[180%] [&_strong]:!font-bold [&_strong]:!text-tokens-neutral-text-secondary [&_ul]:!space-y-0'
                  onTaskLinkClick={onTaskLinkClick}
                >
                  {expandedDetails}
                </ChatMarkdown>
              </Typography>
            )}
            {visibleDetails && (
              <Typography
                fontFamily='geist'
                fontSize='bodySmMedium'
                fontWeight='medium'
                className='w-full text-tokens-neutral-text-secondary'
              >
                <ChatMarkdown
                  className='w-full break-words text-[14px] font-medium leading-[180%] text-tokens-neutral-text-secondary [&>p]:!m-0 [&>ul]:!m-0 [&>p+ul]:!mt-0 [&>ul+p]:!mt-4 [&_li]:!py-0 [&_li]:!font-medium [&_li]:!leading-[180%] [&_p]:!font-medium [&_p]:!leading-[180%] [&_strong]:!font-bold [&_strong]:!text-tokens-neutral-text-secondary [&_ul]:!space-y-0'
                  onTaskLinkClick={onTaskLinkClick}
                >
                  {visibleDetails}
                </ChatMarkdown>
              </Typography>
            )}
            {showCoderFollowUpActions && (
              <div className='flex flex-col gap-3 pt-1' data-testid='coder-summary-actions'>
                <div className='flex items-center gap-3 text-tokens-neutral-text-secondary'>
                  <span className='flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-[var(--status-link-base,rgba(132,111,225,0.10))]'>
                    <img
                      src={FollowUpActionIcon}
                      alt=''
                      className='size-[18px]'
                      draggable={false}
                    />
                  </span>
                  <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium'>
                    Follow-up actions you can do:
                  </Typography>
                </div>
                <div className='flex flex-col gap-2 pl-10'>
                  <button
                    type='button'
                    onClick={handleCodeReviewClick}
                    className='flex min-h-11 w-full items-center gap-3 rounded-[16px] bg-[var(--neutral-fill-Fill-2,rgba(0,0,0,0.02))] px-4 py-2 text-left text-tokens-neutral-text-secondary transition-colors hover:bg-[var(--status-link-base,rgba(132,111,225,0.10))] hover:text-tokens-status-link-3'
                    data-testid='coder-summary-code-review'
                  >
                    <Code2 className='size-4 shrink-0' />
                    <span className='flex min-w-0 flex-1 items-center justify-between gap-3'>
                      <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium'>
                        Code Review
                      </Typography>
                      <span className='truncate text-[14px] leading-5 text-tokens-neutral-text-tertiary'>
                        /code-review
                      </span>
                    </span>
                  </button>
                  <button
                    type='button'
                    onClick={handleFeedbackDraftClick}
                    className='flex min-h-11 w-full items-center gap-3 rounded-[16px] bg-[var(--neutral-fill-Fill-2,rgba(0,0,0,0.02))] px-4 py-2 text-left text-tokens-neutral-text-secondary transition-colors hover:bg-[var(--status-link-base,rgba(132,111,225,0.10))] hover:text-tokens-status-link-3'
                    data-testid='coder-summary-provide-feedback'
                  >
                    <MessageSquare className='size-4 shrink-0' />
                    <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium'>
                      Provide Feedback
                    </Typography>
                  </button>
                  {showRaisePrAction && (
                    <button
                      type='button'
                      onClick={handleRaisePrClick}
                      disabled={isRaisePrPending}
                      aria-busy={isRaisePrPending}
                      className='flex min-h-11 w-full items-center gap-3 rounded-[16px] bg-[var(--neutral-fill-Fill-2,rgba(0,0,0,0.02))] px-4 py-2 text-left text-tokens-neutral-text-secondary transition-colors hover:bg-[var(--status-link-base,rgba(132,111,225,0.10))] hover:text-tokens-status-link-3 disabled:cursor-not-allowed disabled:opacity-60'
                      data-testid='coder-summary-raise-pr'
                    >
                      {isRaisePrPending ? (
                        <Loader2 className='size-4 shrink-0 animate-spin' />
                      ) : (
                        <GitPullRequestCreateArrow className='size-4 shrink-0' />
                      )}
                      <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium'>
                        Raise PR
                      </Typography>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (message.type === 'image_base64' && !isUser) {
    const content = (message.content || '').trim()
    const src = content.startsWith('data:image') ? content : `data:image/png;base64,${content}`
    return (
      <>
        <div className='flex flex-row gap-2.5 items-start justify-start py-0 w-full min-w-0'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMdMedium'
            fontWeight='medium'
            className='text-tokens-neutral-text-primary flex-1 min-w-0 break-words'
          >
            <img
              src={src}
              alt='Image'
              className={cm(
                'w-full h-auto rounded-lg my-3 border border-gray-200 object-contain cursor-zoom-in',
                isCompact ? 'max-w-full max-h-[320px]' : 'max-w-[480px] max-h-[480px]'
              )}
              onClick={() => setIsPreviewOpen(true)}
            />
          </Typography>
        </div>
        <Modal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          hideCloseButton={true}
          className='w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh]'
        >
          <div className='w-full h-full bg-neutral-900/90 overflow-y-auto overflow-x-hidden p-4'>
            <img
              src={src}
              alt='Full view'
              className='w-full h-auto'
              style={{ minWidth: '100%', display: 'block' }}
            />
          </div>
        </Modal>
      </>
    )
  }

  if (message.type === 'canvas' && !isUser) {
    const canvasItems = message.canvasItems as CanvasItem[]
    if (!canvasItems || canvasItems.length === 0) return null

    const handleCanvasItemClick = (canvas: CanvasItem, index: number) => {
      // Check if canvas is editable - open split-screen editor instead of modal viewer
      if (canvas.editable && onEditableCanvasClick) {
        onEditableCanvasClick(canvas)
        return
      }
      // Non-editable canvas - open standard file viewer modal
      setSelectedCanvasIndex(index)
      setCanvasViewerOpen(true)
    }

    return (
      <>
        <div className='flex flex-col gap-2 min-w-0'>
          <CanvasCardsContainer canvasItems={canvasItems} onItemClick={handleCanvasItemClick} />
        </div>
        {shouldShowAssistantFeedback && (
          <div className='mt-4 min-w-0'>
            <AssistantResponseFeedback
              message={feedbackMessage}
              onFeedbackUpdate={onFeedbackUpdate}
              trailingAction={speechActionButton}
            />
          </div>
        )}
        <Modal
          isOpen={canvasViewerOpen}
          onClose={() => setCanvasViewerOpen(false)}
          hideCloseButton={true}
          rawContent={true}
          className='w-[1400px] max-w-[95vw] h-[900px] max-h-[95vh] p-0'
        >
          <CanvasFileViewer
            canvasItems={canvasItems}
            initialSelectedIndex={selectedCanvasIndex}
            onClose={() => setCanvasViewerOpen(false)}
          />
        </Modal>
      </>
    )
  }

  if (message.type === 'selection_widget' && !isUser) {
    const widget = message.widget
    const selectedValues = getSelectedValuesForWidget(widget, selectionWidgetSelections)
    const selectedValueSet = new Set(selectedValues)
    const isMultiSelect = widget.selectionMode === 'MULTI_SELECT'

    const handleToggle = (value: string) => {
      if (isSelectionWidgetDisabled) return

      const nextValues = selectedValueSet.has(value)
        ? selectedValues.filter(selectedValue => selectedValue !== value)
        : isMultiSelect
          ? [...selectedValues, value]
          : [value]

      onSelectionWidgetChange?.(widget, nextValues)
    }

    return (
      <div className='flex w-full min-w-0 flex-col items-start'>
        <div
          className='w-[calc(100%_-_32px)] max-w-[640px] min-w-0 rounded-[8px] border border-tokens-neutral-border-1 bg-tokens-neutral-bg-white/70 p-3 shadow-tokens-shadow-primary'
          data-testid='chat-selection-widget'
          data-disabled={String(isSelectionWidgetDisabled)}
        >
          {(widget.label || widget.placeholder) && (
            <div className='mb-2 flex min-w-0 flex-col gap-1'>
              {widget.label && (
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMdMedium'
                  fontWeight='semibold'
                  className='break-words text-tokens-neutral-text-primary'
                >
                  {widget.label}
                </Typography>
              )}
              {widget.placeholder && (
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='normal'
                  className='break-words text-tokens-neutral-text-tertiary'
                >
                  {widget.placeholder}
                </Typography>
              )}
            </div>
          )}
          <div
            role={isMultiSelect ? 'group' : 'radiogroup'}
            aria-label={widget.label || 'Selection options'}
            className='flex min-w-0 flex-col gap-2'
          >
            {widget.options.map(option => {
              const isSelected = selectedValueSet.has(option.value)
              return (
                <button
                  key={option.value}
                  type='button'
                  role={isMultiSelect ? 'checkbox' : 'radio'}
                  aria-checked={isSelected}
                  onClick={() => handleToggle(option.value)}
                  disabled={isSelectionWidgetDisabled}
                  className={cm(
                    'flex min-h-11 w-full min-w-0 items-start gap-3 rounded-[8px] border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
                    isSelected
                      ? 'border-tokens-status-link-2 bg-tokens-status-link-base text-tokens-neutral-text-primary'
                      : cm(
                          'border-tokens-neutral-border-1 bg-tokens-neutral-bg-white text-tokens-neutral-text-secondary',
                          !isSelectionWidgetDisabled && 'hover:bg-tokens-neutral-fill-fill-6'
                        )
                  )}
                >
                  <span
                    aria-hidden='true'
                    className={cm(
                      'mt-0.5 flex size-5 shrink-0 items-center justify-center border',
                      isMultiSelect ? 'rounded-[6px]' : 'rounded-full',
                      isSelected
                        ? 'border-tokens-status-link-2 bg-tokens-status-link-2 text-tokens-neutral-text-white'
                        : 'border-tokens-neutral-border-2 bg-tokens-neutral-bg-white'
                    )}
                  >
                    {isSelected && <Check className='size-3.5' strokeWidth={2.4} />}
                  </span>
                  <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMdMedium'
                      fontWeight='medium'
                      className='break-words text-inherit'
                    >
                      {option.label}
                    </Typography>
                    {option.description && (
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='normal'
                        className='break-words text-tokens-neutral-text-tertiary'
                      >
                        {option.description}
                      </Typography>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (message.type === 'text') {
    // User text messages are rendered by `UserMessage` (plain text, no
    // markdown); ChatDisplay routes them there by role. Anything reaching this
    // branch is an assistant text message.
    const kickoffTaskId = extractTaskIdFromKickoff(message.content)
    if (
      hiddenTaskKickoffId &&
      kickoffTaskId === hiddenTaskKickoffId &&
      extractGeneratedDetails(message.content)
    ) {
      return null
    }

    if (
      extractGeneratedDetails(message.content) &&
      hasLaterCoderSummaryForTask(allMessages, messageIndex, kickoffTaskId)
    ) {
      return null
    }

    return (
      <div
        className={cm(
          'flex min-w-0 flex-col',
          // Cluster wrapper class lives in the design module so it matches
          // the cluster-end inset used by the PMV branch below.
          assistantTextClusterClass({ isCompact, isSplitPaneLayout })
        )}
      >
        <div
          ref={assistantTextContainerRef}
          className='flex w-full min-w-0 flex-row items-start gap-2.5'
        >
          <Typography
            fontFamily='geist'
            fontSize='bodyMdMedium'
            fontWeight='medium'
            className={cm(
              'text-tokens-neutral-text-primary flex-1 min-w-0 break-words overflow-hidden',
              isVisuallyStreaming && 'chat-streaming'
            )}
          >
            <ChatMarkdown
              className='text-tokens-neutral-text-primary w-full break-words'
              onTaskLinkClick={onTaskLinkClick}
            >
              {message.type === 'text' ? smoothContent : ''}
            </ChatMarkdown>
          </Typography>
        </div>
        {/* CUR-206: feedback (and the TTS button it carries) ONLY renders on
            the cluster's final entry. We deliberately removed the prior
            "render speechActionButton standalone when this isn't the cluster
            end" fallback — for interleaved turns (text → PMV → text → PMV)
            it placed the speaker icon on a mid-turn text segment, visually
            stranding it above the next thinking bubble and detached from
            the thumbs. The cluster-end render path (this branch when text is
            last; the PMV branch when a PMV is last) is the single source of
            truth for response controls. */}
        {shouldShowAssistantFeedback && (
          <AssistantResponseFeedback
            message={feedbackMessage}
            onFeedbackUpdate={onFeedbackUpdate}
            trailingAction={speechActionButton}
          />
        )}
      </div>
    )
  }
  return null
}
